import React, { useReducer } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from './ui/dialog';
import { FileBadge2, Download, ExternalLink, Loader2, Shield, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const initial = { open: false, loading: false, result: null, copied: false };
function reducer(state, action) {
  switch (action.type) {
    case 'OPEN': return { ...state, open: true, result: null };
    case 'CLOSE': return { ...state, open: false, copied: false };
    case 'LOADING': return { ...state, loading: true };
    case 'DONE': return { ...state, loading: false, result: action.payload };
    case 'FAIL': return { ...state, loading: false };
    case 'COPIED': return { ...state, copied: true };
    case 'UNCOPY': return { ...state, copied: false };
    default: return state;
  }
}

export default function ExecutiveDossierButton({ actId, label = 'Gerar Dossiê Executivo' }) {
  const { api } = useAuth();
  const [state, dispatch] = useReducer(reducer, initial);

  const onGenerate = () => {
    dispatch({ type: 'LOADING' });
    api.post(`/dossier/${actId}/generate`)
      .then((res) => {
        dispatch({ type: 'DONE', payload: res.data });
        toast.success('Dossiê Executivo gerado com sucesso');
      })
      .catch((e) => {
        dispatch({ type: 'FAIL' });
        toast.error(e?.response?.data?.detail || 'Erro ao gerar dossiê');
      });
  };

  const onDownload = () => {
    if (!state.result?.pdf_url) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${state.result.pdf_url.replace(/^\/api/, '')}`;
    const token = localStorage.getItem('token') || '';
    // Use fetch to authenticate then trigger download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `dossie_${state.result.dossier_id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => toast.error('Erro ao baixar PDF'));
  };

  const copyVerify = () => {
    if (!state.result?.verify_url) return;
    navigator.clipboard.writeText(state.result.verify_url);
    dispatch({ type: 'COPIED' });
    setTimeout(() => dispatch({ type: 'UNCOPY' }), 1800);
  };

  return (
    <>
      <Button
        onClick={() => dispatch({ type: 'OPEN' })}
        className="bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white shadow-lg"
        data-testid="generate-dossier-btn"
      >
        <FileBadge2 className="w-4 h-4 mr-2" />
        {label}
      </Button>

      <Dialog open={state.open} onOpenChange={(o) => !o && dispatch({ type: 'CLOSE' })}>
        <DialogContent className="sm:max-w-xl" data-testid="dossier-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Shield className="w-5 h-5 text-blue-700" />
              Dossiê Executivo Institucional
            </DialogTitle>
            <DialogDescription>
              Documento institucional consolidado com capa, resumo IA, timeline, plano, evidências, riscos, parecer IA, auditoria e conclusão. Assinado com hash SHA-256 e QR Code de verificação pública.
            </DialogDescription>
          </DialogHeader>

          {!state.result && !state.loading && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
              <p className="text-sm text-slate-700"><strong>O que será incluído:</strong></p>
              <ul className="text-xs text-slate-600 list-disc pl-5 space-y-0.5">
                <li>Capa institucional com selo de severidade</li>
                <li>Resumo executivo gerado por IA</li>
                <li>9 seções: dados, timeline, responsáveis, plano, evidências, riscos, parecer IA, auditoria, conclusão</li>
                <li>QR Code, ID único e hash SHA-256 do PDF</li>
                <li>URL pública de verificação</li>
              </ul>
            </div>
          )}

          {state.loading && (
            <div className="flex items-center gap-3 py-6 text-blue-700" data-testid="dossier-loading">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm font-medium">Gerando dossiê institucional…</span>
            </div>
          )}

          {state.result && (
            <div className="space-y-3" data-testid="dossier-success">
              <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-bold text-emerald-800 mb-2">✅ Dossiê gerado com sucesso</p>
                <dl className="text-xs text-slate-700 space-y-1">
                  <div className="flex justify-between"><dt className="text-slate-500">ID:</dt><dd className="font-mono">{state.result.dossier_id}</dd></div>
                  <div className="flex justify-between"><dt className="text-slate-500">Score:</dt><dd className="font-bold">{state.result.score}/100</dd></div>
                  <div className="flex justify-between gap-2"><dt className="text-slate-500 shrink-0">Hash:</dt><dd className="font-mono text-[10px] truncate">{state.result.hash}</dd></div>
                </dl>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">URL Pública de Verificação</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-slate-700 bg-slate-50 rounded px-2 py-1.5 truncate font-mono" data-testid="dossier-verify-url">
                    {state.result.verify_url}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyVerify} data-testid="copy-verify-btn">
                    {state.copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {!state.result ? (
              <>
                <Button variant="outline" onClick={() => dispatch({ type: 'CLOSE' })} disabled={state.loading}>Cancelar</Button>
                <Button onClick={onGenerate} disabled={state.loading} className="bg-blue-700 hover:bg-blue-800" data-testid="confirm-generate-btn">
                  {state.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileBadge2 className="w-4 h-4 mr-2" />}
                  Gerar Dossiê
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => dispatch({ type: 'CLOSE' })}>Fechar</Button>
                <a
                  href={state.result.verify_url}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex"
                  data-testid="open-verify-link"
                >
                  <Button variant="outline">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Verificar
                  </Button>
                </a>
                <Button onClick={onDownload} className="bg-blue-700 hover:bg-blue-800" data-testid="download-dossier-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Baixar PDF
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
